import { Signal, inject, signal } from '@angular/core';

import { AnchoredPopupStackService } from './anchored-popup-stack.service';

/** Open-state shape shared by every anchored popup: an anchor element plus arbitrary data. */
export interface AnchoredPopupOpenState<T> {
  anchorEl: HTMLElement;
  data: T;
}

/**
 * Base class for a singleton "one popup open at a time" state store. Angular DI
 * cannot inject a generic service directly, so concrete popup services extend
 * this class and add their own `@Injectable({ providedIn: 'root' })` — which is also what
 * gives the `inject()` call below a valid injection context.
 */
export abstract class AnchoredPopupState<T> {
  private readonly stack = inject(AnchoredPopupStackService);
  private readonly _state = signal<AnchoredPopupOpenState<T> | null>(null);
  readonly state: Signal<AnchoredPopupOpenState<T> | null> = this._state.asReadonly();

  open(anchorEl: HTMLElement, data: T): void {
    this.stack.markOpening(this);
    this._state.set({ anchorEl, data });
  }

  close(): void {
    this._state.set(null);
  }

  isOpenFor(anchorEl: HTMLElement): boolean {
    return this._state()?.anchorEl === anchorEl;
  }
}
